import ExtendableObject from "./ExtendableObject";
import { assign } from "./common";

const UploadAdapter = ExtendableObject.extend(
  /** @lends UploadAdapter.prototype */
  {
    /**
     * Adapter realizing all ajax communication
     * @param {object} opts
     * @extends ExtendableObject
     * @constructs
     */
    constructor(opts) {
      ExtendableObject.call(this);
      this.opts = assign(this.getDefaultOptions(), opts);
      this.queue = [];
      this.tasksPending = 0;
    },

    /**
     * Default options factory
     * @returns {object}
     * @protected
     */
    getDefaultOptions() {
      return {};
    },

    /**
     * Almighty ajax function
     * @param {object} opts
     * @param {string} opts.url
     * @param {string} opts.method
     * @param {string|File} opts.data
     * @param {function} opts.cb         - completion callback with arguments (error, result)
     * @param {function} [opts.progress] - progress callback for uploads
     * @private
     */
    ajax({ url, method, data, cb, progress }) {
      this.tasksPending++;

      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = () => {
        if(xhr.readyState === XMLHttpRequest.DONE) {
          if(xhr.status !== 200) cb({ message: `HTTP error ${xhr.status}` });

          this.tasksPending--;

          try {
            const payload = this.getPayload(xhr.responseText);
            const message = this.validatePayload(payload);
            if(message) {
                cb({ message });
            } else {
              cb(null, payload);
              this.nextTask();
            }
          } catch(e) {
            cb(e);
          }
        }
      };

      if(progress && xhr.upload) {
        xhr.upload.onprogress = evt => { if(evt.lengthComputable) progress(evt.loaded / evt.total); };
      }

      xhr.open(method, url, true);

      if(data instanceof File) xhr.setRequestHeader('X-FILENAME', encodeURIComponent(data.name));
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.send(data);
    },

    /**
     * Enqueue and potentially run a task of give type
     * @param {string} type
     * @param {object} opts - opts for {@link UploadAdapter#ajax}
     * @protected
     */
    enqueue(type, opts) {
      const prevLen = this.queue.length;
      this.queue.push({ type, opts });
      if(!prevLen) this.nextTask();
    },

    /**
     * Run next task from queue if not one already in progress
     * @private
     */
    nextTask() {
      if(!this.queue.length) return;
      if(this.tasksPending) return;

      const { opts } = this.queue.shift();

      this.ajax(opts);
    },

    /**
     * Get url for upload action
     * @abstract
     * @protected
     */
    getUploadPath() {
      throw new Error("Uploader: getUploadPath is abstract and should be overridden by a subclass.");
    },

    /**
     * Get url for delete action
     * @param {FileModel} model
     * @abstract
     */
    getDeletePath(model) {
      throw new Error("Uploader: getDeletePath is abstract and should be overridden by a subclass.");
    },

    /**
     * Get url for sort action
     * @param {FileModel[]} models
     * @abstract
     */
    getSortPath(models) {
      throw new Error("Uploader: getSortPath is abstract and should be overridden by a subclass.");
    },

    /**
     * Upload action
     * @param {FileModel} model
     * @param {function} cb         - completion callback, signature `cb(error, result)`
     * @param {function} [progress] - progress callback
     */
    uploadFile(model, cb, progress) {
      const uploadPath = this.getUploadPath();

      if(uploadPath === null) return;

      // quick solution of rate limiting: upload one by one
      // = call enqueue(type, opts) instead of ajax(opts)
      this.enqueue("upload", {
        url: uploadPath,
        method: "POST",
        data: model.file,
        cb,
        progress
      });
    },

    /**
     * Transform a response from server to consumable payload. Meant to be overridden in subclasses.
     * Defaults to identity.
     * All responses are piped through this.
     * @param {string} str - response text
     * @returns {*}
     * @protected
     */
    getPayload(str) {
      return str;
    },

    /**
     * Validate transformed payload (see {@link UploadAdapter#getPayload}).
     * @param {*} payload
     * @returns {*} - An error message on error or falsy value on success
     * @protected
     */
    validatePayload(payload) {
      return 0;
    },

    /**
     * Delete action
     * @param {FileModel} model - model representing the file to delete
     * @param {function} cb     - completion callback, signature `cb(error, result)`
     */
    deleteFile(model, cb) {
      const deletePath = this.getDeletePath(model);

      if(deletePath === null) return;

      this.enqueue("delete", {
        url: deletePath,
        method: "GET",
        cb
      });
    },

    /**
     * Sort action
     * @param {FileModel[]} models - array of models in desired order
     * @param {function} cb        - completion callback, signature `cb(error, result)`
     */
    sortFiles(models, cb) {
      const sortPath = this.getSortPath(models);

      if(sortPath === null) return;

      // delete pending sorts
      for(let i = this.queue.length - 1; i >= 0; i--) if(this.queue[i].type === "sort") this.queue.splice(i, 1);

      this.enqueue("sort", {
        url: sortPath,
        method: "GET",
        cb
      });
    }
  });

export default UploadAdapter;
